import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";

import type {
  AILatencyMetrics,
  AIQuestionAnswer,
  AIStreamingStatus,
  KnowledgeMatch,
} from "@frontierx/shared/types";
import { Loader2, Sparkles } from "lucide-react";

import { AiHelpAsk } from "./AiHelpAsk";
import { AiHelpCardNextPrevious } from "./AiHelpCardNextPrevious";
import { AiHelpLatency } from "./AiHelpLatency";
import { AiHelpSourceMaterial } from "./AiHelpSourceMaterial";

export type HandleGetHelpFunctionArgs = {
  question?: string;
  context?: string;
  transcriptItems?: { id: string }[];
  transcriptItemIds?: string[];
  detectedQuestion?: {
    id: string;
    question: string;
    context: string;
    speaker_id: number;
    start_time: number;
    end_time: number;
    isAnswered: boolean;
    idealAnswer?: string;
    isFromFAQ?: boolean;
  };
};

export type HandleGetHelpFunction = (args: HandleGetHelpFunctionArgs) => void;

export interface AIQuestionAnswerWithSources extends AIQuestionAnswer {
  sourceMaterial?: KnowledgeMatch[];
}

export type AiHelpCardProps = {
  isStreaming: boolean;
  help?: AIQuestionAnswerWithSources;
  currentAiHelpRequest?: AIQuestionAnswerWithSources;
  latencyMetrics?: AILatencyMetrics;
  handlePrevious: () => void;
  handleNext: () => void;
  currentIndex: number;
  historyLength: number;
  handleGetHelp: (args: HandleGetHelpFunctionArgs) => void;
  streamingStatus?: AIStreamingStatus;
};

const AiHelpStreamingStatus = ({
  status,
}: {
  status: AIStreamingStatus | undefined;
}) => {
  const display = {
    started: "Querying",
    detecting_question: "Detecting question",
    searching_faqs: "Searching FAQs",
    found_faq: "Found FAQ match",
    searching_knowledge: "Searching knowledge",
    has_knowledge: "Found knowledge",
    no_knowledge: "No knowledge found",
    generating_answer: "Thinking",
    streaming: "Answering",
    done: "Done",
  };
  if (!status) return null;
  return (
    <div className="text-xs text-gray-400">{display[status] ?? "Working"}…</div>
  );
};

export function AiHelpCard({
  isStreaming,
  help,
  currentAiHelpRequest,
  latencyMetrics,
  handlePrevious,
  handleNext,
  currentIndex,
  historyLength,
  handleGetHelp,
  streamingStatus,
}: AiHelpCardProps) {
  const [cachedAnswer, setCachedAnswer] = useState<string>("");
  const questionDisplay =
    help?.question ?? currentAiHelpRequest?.question ?? "";

  useEffect(() => {
    if (help?.answer) {
      setCachedAnswer(help.answer);
    }
  }, [help?.answer]);

  return (
    <div className="flex w-full flex-col space-y-4 p-4">
      <AiHelpAsk onAskQuestion={handleGetHelp} />

      {(help ?? currentAiHelpRequest) && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-md font-bold">
              {isStreaming ? (
                <Loader2 className="mr-2 inline-block h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 inline-block h-4 w-4" />
              )}{" "}
              {questionDisplay}
            </h2>
            <AiHelpCardNextPrevious
              handlePrevious={handlePrevious}
              handleNext={handleNext}
              currentIndex={currentIndex}
              historyLength={historyLength}
              disabled={isStreaming}
            />
          </div>
          <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
            <div className="p-6">
              {isStreaming && !cachedAnswer ? (
                <div className="flex flex-col space-y-2">
                  <div className="animate-pulse rounded bg-slate-100">
                    &nbsp;
                  </div>
                  <div className="animate-pulse rounded bg-slate-100">
                    &nbsp;
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <ReactMarkdown className="prose max-w-none dark:prose-invert">
                    {cachedAnswer}
                  </ReactMarkdown>
                  {help?.sourceMaterial && (
                    <AiHelpSourceMaterial
                      sourceMaterial={help.sourceMaterial}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <AiHelpStreamingStatus status={streamingStatus} />
          {latencyMetrics && <AiHelpLatency latencyMetrics={latencyMetrics} />}
        </>
      )}
    </div>
  );
}
